/**
 * Animation Observer - Reusable Scroll Animation System
 * 
 * HOW TO USE:
 * 1. Import this file in your section or template
 * 2. Initialize the AnimationObserver class
 * 3. Call observe() with CSS selectors for elements to animate
 * 
 * EXAMPLE USAGE:
 * 
 * // Basic usage
 * const animator = new AnimationObserver();
 * animator.observe('.fade-in-up, .fade-in-left');
 * 
 * // With custom options
 * const animator = new AnimationObserver({
 *   threshold: 0.2,
 *   rootMargin: '0px 0px -100px 0px'
 * });
 * animator.observe('.my-animated-elements');
 * 
 * // Staggered animations
 * animator.observeStaggered('.stagger-children', 100); // 100ms delay between children
 * 
 * ANIMATION CLASSES TO USE IN HTML:
 * - fade-in-up: Fade in from bottom
 * - fade-in-down: Fade in from top  
 * - fade-in-left: Slide in from left
 * - fade-in-right: Slide in from right
 * - fade-in: Simple fade in
 * - scale-in: Scale up from smaller size
 * - stagger-children: Apply staggered animation to child elements
 * - stagger-children-left: Staggered slide in from left
 */

class AnimationObserver {
  constructor(options = {}) {
    this.defaultOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
      ...options
    };
    
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      this.defaultOptions
    );
    
    this.staggerObserver = new IntersectionObserver(
      this.handleStaggeredIntersection.bind(this),
      this.defaultOptions
    );
  }
  
  /**
   * Observe elements for basic animations
   * @param {string} selector - CSS selector for elements to animate
   */
  observe(selector) {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      this.observer.observe(element);
    });
  }
  
  /**
   * Observe elements for staggered child animations
   * @param {string} selector - CSS selector for parent elements
   * @param {number} staggerDelay - Delay between child animations in ms (default: 100)
   */
  observeStaggered(selector, staggerDelay = 100) {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      element.dataset.staggerDelay = staggerDelay;
      this.staggerObserver.observe(element);
    });
  }
  
  /**
   * Handle basic element intersections
   */
  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        this.animateElement(element);
        this.observer.unobserve(element);
      }
    });
  }
  
  /**
   * Handle staggered element intersections
   */
  handleStaggeredIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const element = entry.target;
        const staggerDelay = parseInt(element.dataset.staggerDelay) || 100;
        this.animateStaggeredChildren(element, staggerDelay);
        this.staggerObserver.unobserve(element);
      }
    });
  }
  
  /**
   * Animate a single element
   */
  animateElement(element) {
    // Determine animation speed based on classes
    if (element.classList.contains('animate-fast')) {
      element.classList.add('animate-in-fast');
    } else if (element.classList.contains('animate-slow')) {
      element.classList.add('animate-in-slow');
    } else {
      element.classList.add('animate-in');
    }
  }
  
  /**
   * Animate children with stagger effect
   */
  animateStaggeredChildren(parent, delay = 100) {
    const children = parent.children;
    Array.from(children).forEach((child, index) => {
      setTimeout(() => {
        this.animateElement(child);
      }, index * delay);
    });
  }
  
  /**
   * Manually trigger animation on an element
   * @param {Element|string} element - Element or CSS selector
   * @param {string} animationType - 'fast', 'slow', or 'normal' (default)
   */
  triggerAnimation(element, animationType = 'normal') {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return;
    
    const animationClass = animationType === 'fast' ? 'animate-in-fast' :
                          animationType === 'slow' ? 'animate-in-slow' : 'animate-in';
    
    el.classList.add(animationClass);
  }
  
  /**
   * Reset element animation state
   * @param {Element|string} element - Element or CSS selector
   */
  resetAnimation(element) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return;
    
    el.classList.remove('animate-in', 'animate-in-fast', 'animate-in-slow');
  }
  
  /**
   * Destroy the observer (cleanup)
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.staggerObserver) {
      this.staggerObserver.disconnect();
    }
  }
}

// Auto-initialize if elements with animation classes are found
document.addEventListener('DOMContentLoaded', () => {
  const animatedElements = document.querySelectorAll(`
    .fade-in-up, .fade-in-down, .fade-in-left, .fade-in-right, 
    .fade-in, .scale-in, .reveal-on-scroll
  `);
  
  const staggeredElements = document.querySelectorAll('.stagger-children, .stagger-children-left');
  
  if (animatedElements.length > 0 || staggeredElements.length > 0) {
    const animator = new AnimationObserver();
    
    if (animatedElements.length > 0) {
      animator.observe(`
        .fade-in-up, .fade-in-down, .fade-in-left, .fade-in-right, 
        .fade-in, .scale-in, .reveal-on-scroll
      `);
    }
    
    if (staggeredElements.length > 0) {
      animator.observeStaggered('.stagger-children, .stagger-children-left');
    }
    
    // Make animator globally available
    window.AxiomAnimator = animator;
  }
});

// Export for manual use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AnimationObserver;
}